import { eq, safeEq, safeOnError } from '@rehaje/shared'

import { Abort } from '../constants'
import { addPostEffect, batchDepth, DISPOSED, Effect } from '../signal'

import type { PostEffect } from '../signal'
import type { ReactionOptions, Subscriber } from '../types'
import type { Callback, Disposer, Equals } from '@rehaje/shared'

class Reaction extends Effect {
  _eq: Equals
  _postEffect?: PostEffect

  constructor(
    tracker: Callback,
    subscriber?: Subscriber,
    options?: ReactionOptions
  )
  constructor(
    effect: Callback,
    postEffect?: PostEffect,
    options?: ReactionOptions
  ) {
    super(effect)
    this._onError = safeOnError(options?.onError)
    this._eq = safeEq(options?.equals ?? eq)
    if (postEffect) {
      this._postEffect = postEffect?.bind(undefined)
      this._postEffect._onError = safeOnError(options?.onError)
      this._postEffect._executed = !!options?.fireImmediately
    }
  }

  override _callback(): void {
    const finish = this._start()
    try {
      if (!this._fn || this._flags & DISPOSED) return
      const value = this._fn()
      const subscriber = this._postEffect
      this._version++
      if (
        subscriber &&
        (!subscriber._executed || !this._eq(value, subscriber._oldValue))
      ) {
        subscriber._value = value
        addPostEffect(subscriber)
      }
    } catch (error) {
      if (error !== Abort) {
        if (this._onError) this._onError(error, this._version === 0)
        else throw error
      }
    } finally {
      finish()
    }
  }

  override _dispose(): void {
    super._dispose()
    if (this._postEffect) this._postEffect._disposed = true
  }

  override _notify(): void {
    super._notify()
    if (batchDepth > 0 && this._postEffect) {
      this._postEffect._oldValue = this._postEffect._value
      this._postEffect._executed = true
    }
  }
}

export function reaction<T = any>(
  tracker: Callback<T>,
  subscriber?: Subscriber<T, T>,
  options?: ReactionOptions<T, T>
): Disposer
export function reaction<T = any>(
  tracker: Callback<T>,
  subscriber?: Subscriber<T, T | undefined>,
  options?: ReactionOptions<T, T | undefined> & { fireImmediately: true }
): Disposer
export function reaction(
  tracker: Callback,
  subscriber?: Subscriber,
  options?: ReactionOptions
): Disposer {
  const reaction = new Reaction(tracker, subscriber, options)
  try {
    reaction._callback()
  } catch (error) {
    reaction._dispose()
    throw error
  }
  return reaction._dispose.bind(reaction)
}
